import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import _debounce from "lodash/debounce";
import { defineMessages, FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Button, Link, useToggleState } from "@yoast/ui-library";
import { PlusSmallIcon } from "@heroicons/react/24/solid";
import { PageHeader } from "../PageHeader";
import Search from "../Search/Search";
import SubscriptionsPagination from "../SubscriptionsPage/SubscriptionsPagination";
import useProvisionedSubscriptionList from "../../hooks/useProvisionedSubscriptionList";
import ProvisionedSubscriptionsTable from "./ProvisionedSubscriptionsTable";
import CreateProvisionedSubscriptionModal from "./CreateProvisionedSubscriptionModal";
import ProvisionedSubscriptionResultModal from "./ProvisionedSubscriptionResultModal";
import useProvisionedSubscriptionActions from "./useProvisionedSubscriptionActions";
import * as styles from "./ProvisioningStyles.module.scss";

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 25;

const messages = defineMessages( {
	title: {
		id: "provisioning.list.title",
		defaultMessage: "Provisioning subscriptions",
	},
	introApiLink: {
		id: "provisioning.list.introApiLink",
		defaultMessage: "provisioning API",
	},
	intro: {
		id: "provisioning.list.intro",
		defaultMessage: "These are the Yoast subscriptions your account has provisioned through the {api}, one per customer. Each one gives that customer the products you sold them: a valid license for the Yoast plugins on the site it is connected to, plus the courses and tools the product includes. From here you can create subscriptions, extend, cancel or refund them, and connect a site. Yoast does not bill these customers, so keep them in step with your own billing platform.",
	},
	createButton: {
		id: "provisioning.list.createButton",
		defaultMessage: "New subscription",
	},
	searchLabel: {
		id: "provisioning.list.searchLabel",
		defaultMessage: "Search by customer email",
	},
	searchDescription: {
		id: "provisioning.list.searchDescription",
		defaultMessage: "The list updates as you type.",
	},
} );

/**
 * Reads the search and page from the URL query.
 *
 * @param {string} search The location's search string.
 *
 * @returns {{q: string, page: number}} The list parameters.
 */
function readListParams( search ) {
	const params = new URLSearchParams( search );
	const page = Number.parseInt( params.get( "page" ), 10 );

	return {
		q: params.get( "q" ) || "",
		page: Number.isInteger( page ) && page > 0 ? page : 1,
	};
}

/**
 * Builds the URL query for the list parameters, leaving out the defaults.
 *
 * @param {{q: string, page: number}} listParams The list parameters.
 *
 * @returns {string} The search string, with its leading question mark when not empty.
 */
function buildSearch( { q, page } ) {
	const params = new URLSearchParams();
	if ( q ) {
		params.set( "q", q );
	}
	if ( page > 1 ) {
		params.set( "page", String( page ) );
	}
	const search = params.toString();

	return search ? `?${ search }` : "";
}

/**
 * The provisioned subscriptions list: search, paging, the create flow and the row actions.
 *
 * The search and the page live in the URL so that going back from a subscription's detail page restores
 * the list the user left.
 *
 * @param {Object} props Component props.
 *
 * @returns {JSX.Element} The page.
 */
function ProvisionedSubscriptionsPage( { intl } ) {
	const history = useHistory();
	const location = useLocation();
	const { q, page } = readListParams( location.search );

	// `query` is what the input shows; `q` is what the server has been asked for.
	const [ query, setQuery ] = useState( q );
	const [ isCreateOpen, , , openCreate, closeCreate ] = useToggleState( false );
	const [ created, setCreated ] = useState( null );

	const { items, meta, isLoading, isError, isPlaceholderData } = useProvisionedSubscriptionList( { q, page, limit: PAGE_SIZE } );
	const actions = useProvisionedSubscriptionActions();

	// Typing replaces the URL rather than pushing, so Back does not retrace every keystroke. A new search
	// starts at page 1: the old page number is almost certainly past the end of the new result set.
	const commitSearch = useMemo(
		() => _debounce( value => history.replace( { search: buildSearch( { q: value, page: 1 } ) } ), SEARCH_DEBOUNCE_MS ),
		[ history ],
	);

	useEffect( () => () => commitSearch.cancel(), [ commitSearch ] );

	// Back and Forward move `q` without a keystroke; the input follows, and a search still waiting in the
	// debounce is dropped rather than overwriting the restored URL.
	useEffect( () => {
		commitSearch.cancel();
		setQuery( q );
	}, [ q, commitSearch ] );

	// The result set can shrink under a page number from a bookmark or from Back: the server echoes the
	// page unclamped with no rows, which the table would read as "nothing provisioned yet". Placeholder
	// data describes the previous query and a `totalPages` of 0 is nothing to clamp against.
	const lastPage = ! isPlaceholderData && meta.totalPages > 0 ? meta.totalPages : null;

	useEffect( () => {
		if ( lastPage !== null && page > lastPage ) {
			history.replace( { search: buildSearch( { q, page: lastPage } ) } );
		}
	}, [ history, q, page, lastPage ] );

	const onSearchChange = useCallback( value => {
		setQuery( value );
		commitSearch( value );
	}, [ commitSearch ] );

	const onNavigate = useCallback( nextPage => {
		history.push( { search: buildSearch( { q, page: nextPage } ) } );
	}, [ history, q ] );

	const onCreated = useCallback( subscription => {
		closeCreate();
		setCreated( subscription );
	}, [ closeCreate ] );

	return (
		<div>
			<PageHeader title={ messages.title }>
				<FormattedMessage
					{ ...messages.intro }
					values={ {
						api: (
							<Link href="/provisioning-api/" target="_blank" rel="noopener noreferrer">
								<FormattedMessage { ...messages.introApiLink } />
							</Link>
						),
					} }
				/>
			</PageHeader>
			<section className={ styles.section }>
				<div className={ styles.toolbar }>
					<div className={ styles.toolbarSearch }>
						<Search
							id="provisioning-search"
							descriptionId="provisioning-search-description"
							searchLabel={ intl.formatMessage( messages.searchLabel ) }
							description={ intl.formatMessage( messages.searchDescription ) }
							query={ query }
							onChange={ onSearchChange }
						/>
					</div>
					<Button variant="primary" onClick={ openCreate }>
						<PlusSmallIcon className={ styles.actionIcon } />
						<FormattedMessage { ...messages.createButton } />
					</Button>
				</div>
				<ProvisionedSubscriptionsTable
					subscriptions={ items }
					query={ q }
					isLoading={ isLoading }
					isError={ isError }
					onCreateClick={ openCreate }
					onRenew={ actions.openRenew }
					onSetSite={ actions.openSetSite }
					onCancel={ actions.openCancel }
					onRefund={ actions.openRefund }
				/>
				<SubscriptionsPagination meta={ meta } onNavigate={ onNavigate } />
			</section>

			<CreateProvisionedSubscriptionModal
				isOpen={ isCreateOpen }
				onClose={ closeCreate }
				onCreated={ onCreated }
			/>
			<ProvisionedSubscriptionResultModal
				subscription={ created }
				onClose={ () => setCreated( null ) }
			/>
			{ actions.modals }
		</div>
	);
}

ProvisionedSubscriptionsPage.propTypes = {
	intl: intlShape.isRequired,
};

export default injectIntl( ProvisionedSubscriptionsPage );
